import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, Table, theme } from 'antd';
import { Field } from '../../components/Field';
import { WorkoutPlanType } from '@wellverse/shared-ts';
import type { WorkoutPlanDto } from '@wellverse/shared-ts';

const planTypeColors: Record<WorkoutPlanType, string> = {
  [WorkoutPlanType.AI_SUGGESTED]: '#722ed1',
  [WorkoutPlanType.CUSTOM]: '#1890ff',
  [WorkoutPlanType.KOL_ROUTINE]: '#eb2f96',
  [WorkoutPlanType.GOAL_ROUTINE]: '#faad14',
  [WorkoutPlanType.SEVEN_DAY]: '#13c2c2',
  [WorkoutPlanType.QUICK_LOG]: '#52c41a',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const WorkoutPlanShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<WorkoutPlanDto>({ resource: 'workout-plans' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="workout-plans">
      <div style={{ padding: '8px 0' }}>
        <Field label="Name">{item.name}</Field>
        {item.description && <Field label="Description">{item.description}</Field>}
        <Field label="Plan Type">
          <Tag style={tagStyle(planTypeColors[item.planType] || '#898989')}>{item.planType}</Tag>
        </Field>
        <Field label="Public">
          <Tag style={tagStyle(item.isPublic ? '#3ecf8e' : '#898989')}>
            {item.isPublic ? 'Yes' : 'No'}
          </Tag>
        </Field>
        {item.userId && <Field label="User ID">{item.userId}</Field>}
        {item.schedule && Object.keys(item.schedule).length > 0 && (
          <Field label="Schedule">
            <pre style={{ background: token.colorBgContainerDisabled, padding: 12, borderRadius: 8, overflow: 'auto' }}>
              {JSON.stringify(item.schedule, null, 2)}
            </pre>
          </Field>
        )}
        <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
        <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
      </div>

      <h3 style={{ color: token.colorText, margin: '24px 0 16px' }}>Exercises</h3>
      <Table
        dataSource={item.exercises}
        rowKey="id"
        pagination={false}
        size="small"
      >
        <Table.Column
          dataIndex="exercise"
          title="Exercise Name"
          render={(exercise: { name: string } | undefined) => exercise?.name || '—'}
        />
        <Table.Column dataIndex="orderIndex" title="Order" />
        <Table.Column dataIndex="targetSets" title="Sets" />
        <Table.Column dataIndex="targetReps" title="Reps" />
        <Table.Column
          dataIndex="targetWeightKg"
          title="Weight (kg)"
          render={(v: number | undefined) => v != null ? `${v} kg` : '—'}
        />
        <Table.Column
          dataIndex="restSeconds"
          title="Rest"
          render={(v: number | undefined) => v != null ? `${v}s` : '—'}
        />
        <Table.Column
          dataIndex="notes"
          title="Notes"
          render={(v: string | undefined) => v || '—'}
        />
      </Table>
    </Show>
  );
};
